import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const STYLE = {
  overdue: "bg-red-100 text-red-800 border-red-200",
  "due today": "bg-amber-100 text-amber-800 border-amber-200",
  "to fit": "bg-sky-100 text-sky-800 border-sky-200",
  out: "bg-cream-100 text-ink border-bronze-light/50",
  returned: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }) : "—");
const dayLabel = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", timeZone: "UTC" });

const Stat = ({ k, label, value, tone = "" }) => (
  <div><div className={`font-serif text-3xl ${tone}`} data-testid={`stock-${k}`}>{value}</div><div className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">{label}</div></div>
);

const StockCard = ({ kind, s, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [n, setN] = useState(s.total);
  return (
    <div className="border border-stone-200 rounded-md p-4" data-testid={`stock-card-${kind}`}>
      <div className="flex items-center gap-2">
        <h3 className="font-serif text-xl">{s.label} monitors</h3>
        {edit
          ? <span className="ml-auto flex items-center gap-2 text-sm">
              <input type="number" min={0} max={50} value={n} onChange={(e) => setN(e.target.value)} className="w-16 rounded-sm border border-stone-300 px-2 py-1" data-testid={`stock-input-${kind}`} />
              <button type="button" className="underline" onClick={() => (n === "" || Number(n) < 0 ? toast.error("Please enter a number of monitors.") : onSave(kind, Number(n)).then(() => setEdit(false)))} data-testid={`stock-save-${kind}`}>Save</button>
            </span>
          : <button type="button" className="ml-auto text-xs underline" onClick={() => { setN(s.total); setEdit(true); }} data-testid={`stock-edit-${kind}`}>Change number owned</button>}
      </div>
      <div className="mt-3 grid grid-cols-4 gap-3">
        <Stat k={`${kind}-total`} label="Owned" value={s.total} />
        <Stat k={`${kind}-in`} label="In clinic" value={s.in_clinic} tone="text-emerald-700" />
        <Stat k={`${kind}-out`} label="Out" value={s.out} />
        <Stat k={`${kind}-overdue`} label="Overdue" value={s.overdue} tone={s.overdue ? "text-red-700" : ""} />
      </div>
      <div className="mt-4 text-[11px] uppercase tracking-[0.14em] text-ink-muted">Free to fit, next 14 days</div>
      <div className="mt-1 flex flex-wrap gap-1" data-testid={`stock-days-${kind}`}>
        {s.days.map((d) => (
          <span key={d.date} title={`${d.free} free`} className={`text-[11px] px-1.5 py-1 rounded border text-center w-[52px] ${d.free === 0 ? "bg-red-50 border-red-200 text-red-800" : "bg-white border-stone-200"}`}>
            {dayLabel(d.date)}<b className="block text-sm">{d.free}</b>
          </span>
        ))}
      </div>
    </div>
  );
};

const Row = ({ m, onToggle }) => {
  const id = `${m.id}-${m.kind}`;
  return (
    <tr className="border-t border-stone-200/70" data-testid={`monitor-row-${id}`}>
      <td className="py-2.5 pr-3"><span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${STYLE[m.state]}`} data-testid={`monitor-state-${id}`}>{m.state}</span></td>
      <td className="py-2.5 pr-3 font-medium">{m.label}</td>
      <td className="py-2.5 pr-3"><div>{m.patient_name}</div><div className="text-xs text-ink-muted">{m.booking_ref} · {m.days} day{m.days > 1 ? "s" : ""}</div></td>
      <td className="py-2.5 pr-3 whitespace-nowrap text-ink-muted">{when(m.fitted_at)}</td>
      <td className="py-2.5 pr-3 whitespace-nowrap font-medium">{when(m.return_at)}{m.overdue_emailed_at && <div className="text-[11px] text-red-700 font-normal">Overdue email sent</div>}</td>
      <td className="py-2.5 text-right whitespace-nowrap">
        {m.state === "to fit" ? <span className="text-xs text-ink-muted">Not fitted yet</span> : m.returned_at
          ? <span className="text-xs text-ink-muted">Back {when(m.returned_at)} <button type="button" className="underline ml-2" onClick={() => onToggle(m, false)} data-testid={`monitor-undo-${id}`}>Undo</button></span>
          : <button type="button" className="btn-secondary !py-1 !px-3 text-sm" onClick={() => onToggle(m, true)} data-testid={`monitor-returned-${id}`}>Mark returned</button>}
      </td>
    </tr>
  );
};

export const MonitorStock = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => { api.get("/admin/devices").then(({ data }) => { setData(data); setFailed(false); }).catch(() => setFailed(true)); }, []);
  useEffect(load, [load]);
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  const toggle = (m, returned) => api.post(`/admin/devices/returns/${encodeURIComponent(m.id)}/${m.kind}`, { returned })
    .then(() => { toast.success(returned ? "Marked as returned - reminders stopped." : "Marked as still out."); load(); }).catch(err);
  const save = (kind, n) => api.put("/admin/devices", { ...Object.fromEntries(Object.entries(data.summary).map(([k, v]) => [k, v.total])), [kind]: n })
    .then(() => { toast.success("Number of monitors updated."); load(); }).catch(err);
  if (failed) return <p className="mt-8 text-sm text-destructive" data-testid="monitor-stock-error">Couldn&apos;t load the monitor counts. <button type="button" className="underline" onClick={load}>Try again</button></p>;
  if (!data) return null;
  const overdue = Object.values(data.summary).reduce((a, s) => a + s.overdue, 0);
  return (
    <section className="mt-8 bg-white border border-stone-200/70 rounded-md p-5 overflow-x-auto" data-testid="monitor-stock">
      <div className="flex items-baseline gap-3">
        <h2 className="font-serif text-2xl">Monitors</h2>
        {overdue > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white" data-testid="monitor-overdue-count">{overdue} overdue</span>}
      </div>
      <p className="text-xs text-ink-muted mt-1">Online and staff booking only offer Holter or ABPM times when a monitor is free. Overdue monitors count as out until marked returned. Patients get reminders the evening before and morning of the return date, and an overdue email the next morning.</p>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {Object.entries(data.summary).map(([k, s]) => <StockCard key={`${k}-${s.total}`} kind={k} s={s} onSave={save} />)}
      </div>
      {data.monitors.length === 0
        ? <p className="text-sm text-ink-muted mt-4" data-testid="monitor-empty">No monitors out at the moment.</p>
        : <table className="mt-4 w-full text-sm text-left"><thead><tr className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">
            <th className="py-2 font-medium">Status</th><th className="font-medium">Type</th><th className="font-medium">Patient</th><th className="font-medium">Fitted</th><th className="font-medium">Due back</th><th /></tr></thead>
            <tbody>{data.monitors.map((m) => <Row key={`${m.id}-${m.kind}`} m={m} onToggle={toggle} />)}</tbody></table>}
    </section>
  );
};
